import baseAxios from 'axios';
import { getJWT } from '@bynder/js-access-token-lib';

import * as bynderAxios from './customAxios';
import {
	Cart,
	Language,
	NavItem,
	TreatmentKeys,
	UserAccount,
	UserData,
	PortalHomepage,
} from '../types';
import { getCookieValue } from './cookie';

const defaultNavigationItemsOnError: NavItem[] = [
	{
		id: '1',
		url: '/',
		label: 'Home',
	},
	{
		id: '2',
		url: '/media/',
		label: 'Assets',
	},
	{
		id: '3',
		url: '/collections/',
		label: 'Collections',
	},
];

export const API = {
	getCurrentUser,
	getPortalFeatures: (portalId: string): Promise<string[]> =>
		bynderAxios
			.get({ url: `/v7/portal/portals/${portalId}/features/`, jwt: true })
			.then((response) => response.features)
			.catch(() => []),
	getPortalHomepage: (): Promise<PortalHomepage> =>
		bynderAxios.get({ url: '/v7/portal/homepage/', jwt: true }),
	getAbilities: (uuid: string) =>
		bynderAxios.get({ url: `/v7/menu/abilities?uuid=${uuid}`, jwt: true }),
	getAbilitiesPublic: (uuid: string) =>
		bynderAxios.get({ url: `/v7/menu/abilities_public?uuid=${uuid}` }),
	getImpersonate: (): Promise<boolean> => bynderAxios.get({ url: '/user/is_impersonating' }),
	getNavigation: (uuid: string): Promise<NavItem[]> => {
		return bynderAxios
			.get({ url: getMenuRequestUrl(uuid), jwt: true })
			.catch(() => defaultNavigationItemsOnError);
	},
	getLanguages: (uuid: string): Promise<Language[]> =>
		bynderAxios.get({ url: getLanguagesRequestUrl(uuid), jwt: true }),
	getBrandstoreCart: (): Promise<Cart> => bynderAxios.get({ url: '/user/brandstore_cart' }),

	getFeatureTreatment: (
		feature: TreatmentKeys,
		isCacheDisabled: boolean,
		attributes?: Record<string, unknown>,
	): Promise<{ treatment: string }> => {
		return bynderAxios.get({
			url: `/v7/featuretreatments/treatment/${feature}`,
			config: {
				headers: isCacheDisabled ? bynderAxios.headersNoCache : {},
				params: attributes ? { attributes: JSON.stringify(attributes) } : undefined,
			},
			jwt: true,
		});
	},

	getAccounts: (input: string): Promise<UserAccount[]> => {
		return bynderAxios.get({ url: '/account/suggest/', config: { params: { term: input } } });
	},
};

export const getCurrentLocale = () => {
	return getCookieValue('DEFAULTLOCALE') || 'en-US';
};

const getLanguagesRequestUrl = (uuid: string) => {
	return `/v7/menu/languages?include_display_names=true&locale=${getCurrentLocale()}&uuid=${uuid}`;
};

const getMenuRequestUrl = (uuid: string) => {
	return `/v7/menu/menu?locale=${getCurrentLocale()}&uuid=${uuid}`;
};

/*
 * Use baseAxios to locally handle 401 status code response
 */
async function getCurrentUser(): Promise<UserData | null> {
	const jwtToken = await getJWT();

	return baseAxios
		.get('/v7/identity/users/current_user/', {
			headers: { ...bynderAxios.headersDefault, Authorization: 'Bearer ' + jwtToken },
			validateStatus: (status) =>
				baseAxios.defaults.validateStatus?.(status) || status === 401,
		})
		.then((response) => (response.status === 401 ? null : response.data))
		.catch(bynderAxios.onError);
}
